import { useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';

import Box from '~/common/components/Box';
import Button from '~/common/components/Button';
import Flex from '~/common/components/Flex';
import Spinner from '~/common/components/Spinner';
import Text from '~/common/components/Texts/Text';
import { toTitleCase } from '~/common/util/format';

import { AuditHistoryPageEnum, PageTitleMap } from './constants';
import language from './language.json';

type LangType = typeof language;

const TealButton = styled(Button)`
  margin-top: 1rem;
  margin-left: auto;
  border: 1px solid ${(props) => props.theme.colors?.teal[200]};
  background-color: ${(props) => props.theme.colors?.teal[100]};
  color: ${(props) => props.theme.colors?.white};

  &:hover {
    background-color: ${(props) => props.theme.colors?.teal[100]};
  }
`;

const Container = styled(Box)`
  padding: 1.5rem 1rem;
`;

const StatusText = styled(Text)`
  padding: 16px 10px 16px 16px;
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.5rem;
`;

function getPageTitle(pageEnum: string, category?: string) {
  const pageTitle = PageTitleMap[pageEnum] ?? PageTitleMap[0];
  if (pageTitle === 'Unknown' && category === undefined) {
    return 'Unknown';
  }
  if (pageEnum === AuditHistoryPageEnum.VA_CAIVRS.toString()) {
    return pageTitle;
  }
  if (category !== undefined) {
    return toTitleCase(category.replaceAll('-', ' '));
  }
  return pageTitle;
}

type LayoutProps = {
  pageEnum?: string;
  children?: React.ReactNode;
  headerContent?: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  isDataEmpty?: boolean;
  category?: string;
  customTitle?: string;
};

export default function Layout({
  children,
  headerContent,
  pageEnum,
  isError,
  isLoading,
  isDataEmpty,
  category,
  customTitle,
}: Readonly<LayoutProps>) {
  const theme = useTheme();
  const isSummary1003AuditPage = category !== undefined;

  if (isLoading) {
    return (
      <Container component="section">
        {headerContent}
        <Spinner />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container component="section">
        {headerContent}
        <StatusText>{language.error}</StatusText>
      </Container>
    );
  }

  const pageTitle = getPageTitle(pageEnum, category);
  const getSubheading = (
    isSummary: boolean,
    language: LangType,
    pageTitle: string,
    category: string,
  ) => {
    const isCaivrs = pageEnum === AuditHistoryPageEnum.VA_CAIVRS.toString();
    let subheadingTemplate = language.subheading;
    if (isSummary && !isCaivrs) {
      subheadingTemplate = language.subheadingSummary;
    }

    subheadingTemplate = subheadingTemplate.replaceAll(
      '{{PAGE_TITLE}}',
      pageTitle,
    );

    if (isSummary) {
      subheadingTemplate = subheadingTemplate.replaceAll(
        '{{CATEGORY}}',
        toTitleCase(category.replaceAll('-', ' ')),
      );
    }

    return subheadingTemplate;
  };

  const auditHistoryNotFound = language.notfound.replaceAll(
    '{{PAGE_TITLE}}',
    pageTitle,
  );

  return (
    <Container component="section">
      {headerContent}
      <Text
        as="h2"
        data-testid="page-title"
        color="blue.700"
        borderBottom="2px solid"
        borderColor={theme.colors?.teal[100]}
      >
        {customTitle ? (
          <Text as="span" textTransform="capitalize">
            {customTitle}
          </Text>
        ) : (
          [
            <Text key="page-title" as="span" textTransform="capitalize">
              {pageTitle} {isSummary1003AuditPage && 'Summary'}
            </Text>,
            <Text
              key="title-separator"
              as="span"
              color={theme.colors?.teal[100]}
              px={2}
            >
              |
            </Text>,
            language.heading,
          ]
        )}
      </Text>
      <Text as="p" py={4}>
        {getSubheading(isSummary1003AuditPage, language, pageTitle, category)}
      </Text>

      {isDataEmpty ? <StatusText>{auditHistoryNotFound}</StatusText> : children}

      <Flex>
        <TealButton
          onClick={() => {
            window.close();
          }}
        >
          Done
        </TealButton>
      </Flex>
    </Container>
  );
}
